/**
 * Microsoft Graph API Service
 * Handles M365 content search and retrieval
 */

import { getAccessToken } from './authService';
import { throwIfAborted, isUserCancellation } from '@/utils/cancellation';
import { azureConfig } from '@/config/azureConfig';
import { API_CONFIG, ERROR_MESSAGES } from '@/utils/constants';
import type {
  GraphSearchRequest,
  GraphSearchResult,
  GraphSearchHit,
  UserProfile,
  GraphError,
} from '@/types';

const GRAPH_ENDPOINT = azureConfig.graph.endpoint;

/**
 * Make authenticated request to Microsoft Graph
 */
async function graphRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();

  const response = await fetch(`${GRAPH_ENDPOINT}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error: GraphError = await response.json();
    throw new Error(
      error.error?.message || ERROR_MESSAGES.GRAPH_API_ERROR
    );
  }

  return response.json();
}

/**
 * Get current user profile
 */
export async function getUserProfile(): Promise<UserProfile> {
  return graphRequest<UserProfile>('/me');
}

/**
 * Search Microsoft 365 content
 */
export async function searchDocuments(
  query: string,
  size: number = API_CONFIG.GRAPH_SEARCH_SIZE,
  signal?: AbortSignal
): Promise<GraphSearchResult> {
  const searchRequest: GraphSearchRequest = {
    entityTypes: ['driveItem', 'listItem', 'site'],
    query: {
      queryString: query,
    },
    from: 0,
    size: Math.min(size, API_CONFIG.GRAPH_SEARCH_MAX_SIZE),
    fields: [
      'name',
      'webUrl',
      'lastModifiedDateTime',
      'createdDateTime',
      'summary',
    ],
  };

  const body = {
    requests: [searchRequest],
  };

  return graphRequest<GraphSearchResult>('/search/query', {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  });
}

/**
 * Get file content from drive
 */
export async function getFileContent(
  driveId: string,
  itemId: string
): Promise<string> {
  const endpoint = `/drives/${driveId}/items/${itemId}/content`;
  const token = await getAccessToken();

  const response = await fetch(`${GRAPH_ENDPOINT}${endpoint}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch file content');
  }

  // Get content as text
  return response.text();
}

/**
 * Build context string from search results
 */
export function buildContextFromSearchResults(
  hits: GraphSearchHit[]
): string {
  if (hits.length === 0) {
    return '';
  }

  const contextParts = hits.map((hit, index) => {
    const resource = hit.resource;
    return `
[M365 ${index + 1}] ${resource.name}
URL: ${resource.webUrl}
Summary: ${hit.summary || 'No summary available'}
Last Modified: ${resource.lastModifiedDateTime}
---`;
  });

  return `
Microsoft 365 documents:

${contextParts.join('\n')}
`;
}

/**
 * Search with retry logic
 */
export async function searchWithRetry(
  query: string,
  maxRetries: number = API_CONFIG.RETRY_ATTEMPTS,
  signal?: AbortSignal
): Promise<GraphSearchResult> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // A Stop pressed between attempts (or before the first) short-circuits
    // the loop instead of burning the remaining retries + backoff.
    throwIfAborted(signal);
    try {
      return await searchDocuments(query, undefined, signal);
    } catch (error) {
      // User cancel is terminal — propagate it so the chat turn aborts
      // rather than retrying an already-aborted request.
      if (isUserCancellation(error, signal)) {
        throw error;
      }

      lastError = error as Error;
      console.error(`Search attempt ${attempt + 1} failed:`, error);

      if (attempt < maxRetries - 1) {
        // Wait before retrying (exponential backoff)
        const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error(ERROR_MESSAGES.GRAPH_API_ERROR);
}

/**
 * Return the subset of `candidateGroupIds` the current user is a member
 * of. Uses POST /me/checkMemberGroups, which does the membership
 * intersection on the Graph side — works regardless of how many groups
 * the user belongs to (we have customers in 4000+ groups) and never
 * paginates. Takes 1 request per chunk of 20 IDs (Graph's per-call cap).
 *
 * This replaces the old "fetch ALL groups via /me/memberOf, then filter
 * client-side" approach, which had two problems for high-group-count
 * tenants:
 *   1. /me/memberOf returns 100 items per page by default; without
 *      paginating, role-mapped groups falling on page 2+ were silently
 *      missed and users failed the group-membership check.
 *   2. Even with pagination, sending the full membership list in the
 *      X-User-Groups header (~36 chars per id × 4000 ids ≈ 150 KB) blew
 *      past the 8–16 KB header limit most reverse proxies enforce, so
 *      backend role extraction lost the header and fell back to 'user'.
 *
 * Both call sites — AuthContext for frontend role mapping, agentService
 * for the X-User-Groups backend hint — only ever care about the mapped
 * subset, which is what this function returns.
 */
export async function checkUserMappedGroups(
  candidateGroupIds: string[],
): Promise<string[]> {
  if (candidateGroupIds.length === 0) return [];

  const memberOf: string[] = [];
  // Graph caps /me/checkMemberGroups at 20 IDs per call. In practice
  // tenants have a handful of mapped groups so this loop is one
  // iteration, but chunking keeps us correct if that ever grows.
  for (let i = 0; i < candidateGroupIds.length; i += 20) {
    const chunk = candidateGroupIds.slice(i, i + 20);
    try {
      const response = await graphRequest<{ value: string[] }>(
        '/me/checkMemberGroups',
        {
          method: 'POST',
          body: JSON.stringify({ groupIds: chunk }),
        },
      );
      memberOf.push(...(response.value ?? []));
    } catch (error) {
      console.error(
        '[graphService] checkMemberGroups failed for chunk:',
        error,
      );
      // Continue with remaining chunks rather than discarding everything.
    }
  }
  return memberOf;
}

/**
 * Fetch the signed-in user's `department` (and incidentally `jobTitle`
 * / `officeLocation`) from Microsoft Graph. Used to populate the
 * `users.department` column on the backend so the dashboard's
 * Departments page rolls up usage by org without admins editing each
 * user manually.
 *
 * Returns `null` on any failure — Graph occasionally rejects this call
 * for service / synthetic accounts and we never want a department-fetch
 * error to break sign-in. The backend treats a missing / null value
 * as "leave the existing column alone".
 *
 * Mirrors the dashboard's `dashboard/src/services/graphService.ts:getUserDepartment`.
 */
export async function getUserDepartment(): Promise<string | null> {
  try {
    const profile = await graphRequest<{
      department?: string | null;
      jobTitle?: string | null;
      officeLocation?: string | null;
    }>('/me?$select=department,jobTitle,officeLocation');
    const dept = profile.department?.trim();
    return dept && dept.length > 0 ? dept : null;
  } catch (error) {
    console.warn('[graphService] getUserDepartment failed:', error);
    return null;
  }
}

/**
 * Map user's Azure AD groups to application role
 * Returns the highest priority role based on group mappings
 */
export function mapGroupsToRole(
  userGroupIds: string[],
  groupMappings: Array<{ groupId: string; role: 'admin' | 'power_user' | 'user' }>
): 'admin' | 'power_user' | 'user' {
  // Priority order: admin > power_user > user
  const rolePriority = { admin: 3, power_user: 2, user: 1 };
  
  let highestRole: 'admin' | 'power_user' | 'user' = 'user';
  let highestPriority = 0;
  
  for (const groupId of userGroupIds) {
    const mapping = groupMappings.find((m) => m.groupId === groupId);
    if (mapping) {
      const priority = rolePriority[mapping.role];
      if (priority > highestPriority) {
        highestRole = mapping.role;
        highestPriority = priority;
      }
    }
  }
  
  return highestRole;
}
